import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { productsAPI } from '../../utils/api';
import useCartStore from '../../context/cartStore';
import { localized } from '../../utils/localized';
import toast from 'react-hot-toast';

const ProductPage = () => {
  const { slug } = useParams();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedVariant, setSelectedVariant] = useState(null);
  const [activeImage, setActiveImage] = useState(0);
  const [qty, setQty] = useState(1);
  const { addItem } = useCartStore();
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const isAr = lang === 'ar';

  // Currency formatter — KD prefix for English, د.ك suffix for Arabic.
  // Numbers stay LTR inside RTL by their nature; placement just flips.
  const formatPrice = (n) => {
    const v = Number.isFinite(n) ? n.toFixed(3) : '0.000';
    return isAr ? `${v} د.ك` : `KD ${v}`;
  };

  useEffect(() => {
    setLoading(true);
    productsAPI.getBySlug(slug).then(data => {
      setProduct(data);
      if (data.variants?.length) setSelectedVariant(data.variants[0]);
    }).finally(() => setLoading(false));
  }, [slug]);

  // Use the localized name in toast + alt text
  const displayName = product ? (localized(product, 'name', lang) || product.name) : '';

  const handleAddToCart = () => {
    addItem(product, selectedVariant, qty);
    toast.success(`${displayName} ${t('product.added')}`, { icon: '🍯' });
  };

  if (loading) return (
    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
      <div className="spinner" />
    </div>
  );

  if (!product) return (
    <div style={{ textAlign: 'center', padding: '80px 20px' }} dir={isAr ? 'rtl' : 'ltr'}>
      <h2>{t('product.notFound')}</h2>
      <Link to="/shop" className="btn btn-primary" style={{ marginTop: 20 }}>{t('product.backToShop')}</Link>
    </div>
  );

  const price = selectedVariant ? parseFloat(selectedVariant.price) : parseFloat(product.base_price);
  // Build unified media list: images + optional video at the end
  const imageMedia = (product.images?.length ? product.images : [{ image_url: null }])
    .map(img => ({ type: 'image', url: img.image_url }));
  const videoMedia = product.video_url ? [{ type: 'video', url: product.video_url }] : [];
  const media = [...imageMedia, ...videoMedia];
  const activeMedia = media[activeImage] || media[0];

  // Localized category label (uses category_name_ar when available)
  const displayCategory = product.category_name
    ? (isAr && product.category_name_ar ? product.category_name_ar : product.category_name)
    : '';

  return (
    <div className="product-page" dir={isAr ? 'rtl' : 'ltr'}>
      <div className="container">
        {/* Breadcrumb */}
        <nav className="breadcrumb">
          <Link to="/">{t('product.home')}</Link> / <Link to="/shop">{t('product.shop')}</Link> / {displayName}
        </nav>

        <div className="product-detail">
          {/* Media gallery */}
          <div className="product-detail__gallery">
            <div className="product-detail__main-img">
              {activeMedia?.type === 'video' && activeMedia.url ? (
                <video key={activeMedia.url} src={activeMedia.url} controls playsInline preload="metadata" />
              ) : activeMedia?.url ? (
                <img src={activeMedia.url} alt={displayName} />
              ) : (
                <div className="product-detail__placeholder">🍯</div>
              )}
            </div>
            {media.length > 1 && (
              <div className="product-detail__thumbs">
                {media.map((m, i) => (
                  <button key={i} className={`product-detail__thumb ${activeImage === i ? 'active' : ''}`}
                    onClick={() => setActiveImage(i)} aria-label={m.type === 'video' ? 'Play video' : `Image ${i + 1}`}>
                    {m.type === 'video' ? (
                      <>
                        <video src={m.url} muted preload="metadata" />
                        <span className="product-detail__thumb-play" aria-hidden="true">
                          <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                            <path d="M8 5v14l11-7z"/>
                          </svg>
                        </span>
                      </>
                    ) : m.url ? (
                      <img src={m.url} alt="" />
                    ) : (
                      <span>🍯</span>
                    )}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Info */}
          <div className="product-detail__info">
            {displayCategory && <p className="product-detail__category">{displayCategory}</p>}
            <h1 className="product-detail__name">{displayName}</h1>
            {/* Show the alternate-language name as a subtitle: AR underneath EN, EN underneath AR */}
            {isAr
              ? (product.name && product.name !== displayName && <p className="product-detail__name-ar" style={{ direction: 'ltr' }}>{product.name}</p>)
              : (product.name_ar && <p className="product-detail__name-ar">{product.name_ar}</p>)
            }

            {/* Rating */}
            {product.avg_rating > 0 && (
              <div className="product-detail__rating">
                <span className="stars">{'★'.repeat(Math.round(product.avg_rating))}</span>
                <span className="count">{t('product.reviewCount', { count: product.review_count })}</span>
              </div>
            )}

            {/* Price */}
            {(() => {
              const cmp = parseFloat(selectedVariant?.compare_price ?? product.compare_price) || 0;
              const showCompare = cmp > price && price > 0;
              return (
                <div className="product-detail__price">
                  <span className="price-main">{formatPrice(price)}</span>
                  {showCompare && (
                    <>
                      <span className="price-compare">{formatPrice(cmp)}</span>
                      <span className="price-discount">-{Math.round((1 - price / cmp) * 100)}%</span>
                    </>
                  )}
                </div>
              );
            })()}

            {/* Short Description */}
            {product.short_description && (
              <p className="product-detail__short-desc">{product.short_description}</p>
            )}

            {/* Variants */}
            {product.variants?.length > 0 && (
              <div className="product-detail__variants">
                <p className="variant-label">{product.variants[0].variant_name}:</p>
                <div className="variant-options">
                  {product.variants.map(v => (
                    <button key={v.id}
                      className={`variant-btn ${selectedVariant?.id === v.id ? 'active' : ''}`}
                      onClick={() => setSelectedVariant(v)}>
                      {v.variant_value}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Quantity & Add to Cart */}
            {(() => {
              // Stock comes from selected variant if any, else from product itself
              const rawStock = selectedVariant?.stock_quantity ?? product.stock_quantity;
              const stock = Number.isFinite(parseInt(rawStock, 10)) ? parseInt(rawStock, 10) : null;
              const isOut = stock !== null && stock <= 0;
              const isLow = stock !== null && stock > 0 && stock <= 5;
              const max   = stock !== null ? stock : Infinity;
              return (
                <>
                  <div className="product-detail__actions">
                    <div className="qty-control">
                      <button onClick={() => setQty(q => Math.max(1, q - 1))} disabled={isOut}>−</button>
                      <span>{qty}</span>
                      <button onClick={() => setQty(q => Math.min(max, q + 1))} disabled={isOut || qty >= max}>+</button>
                    </div>
                    <button
                      className="btn btn-primary"
                      style={{ flex: 1, opacity: isOut ? 0.6 : 1, cursor: isOut ? 'not-allowed' : 'pointer' }}
                      onClick={handleAddToCart}
                      disabled={isOut}
                    >
                      🍯 {isOut
                        ? t('product.outOfStock')
                        : t('product.addToBasketWithPrice', { amount: formatPrice(price * qty) })}
                    </button>
                  </div>
                  {stock !== null && (
                    <p className={`product-detail__stock ${isOut ? 'out' : isLow ? 'low' : 'ok'}`}>
                      {isOut
                        ? `⚠ ${t('product.outOfStock')}`
                        : isLow
                          ? `🔥 ${t('product.lowStock', { count: stock })}`
                          : `✓ ${t('product.stockCount', { count: stock })}`}
                    </p>
                  )}
                </>
              );
            })()}

            {/* Meta details */}
            <div className="product-detail__meta">
              {product.origin_country && <div><strong>{t('product.origin')}:</strong> {product.origin_country}</div>}
              {product.harvest_year && <div><strong>{t('product.harvestYear')}:</strong> {product.harvest_year}</div>}
              {product.sku && <div><strong>{t('product.sku')}:</strong> {product.sku}</div>}
            </div>

            {/* Trust badges */}
            <div className="product-detail__trust">
              <span>🔒 {t('product.secureCheckout')}</span>
              <span>🚚 {t('product.kuwaitDelivery')}</span>
              <span>✅ {t('product.authentic')}</span>
            </div>
          </div>
        </div>

        {/* Description */}
        {(product.description || product.description_ar) && (
          <div className="product-detail__description">
            <h3>{t('product.aboutThisHoney')}</h3>
            <p>{(isAr && product.description_ar) ? product.description_ar : product.description}</p>
          </div>
        )}

        {/* Reviews */}
        {product.reviews?.length > 0 && (
          <div className="product-detail__reviews">
            <h3>{t('product.customerReviews')}</h3>
            <div className="reviews-list">
              {product.reviews.map(r => (
                <div key={r.id} className="review-card">
                  <div className="review-card__header">
                    <strong>{r.first_name || r.guest_name || t('product.anonymous')}</strong>
                    <span className="review-stars">{'★'.repeat(r.rating)}</span>
                    {r.is_verified_purchase === 1 && <span className="badge badge-olive">{t('product.verifiedPurchase')}</span>}
                  </div>
                  {r.title && <p className="review-title">{r.title}</p>}
                  <p className="review-body">{r.body}</p>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      <style>{`
        .product-page { padding: 30px 0 80px; }
        .breadcrumb { font-size: 0.82rem; color: var(--text-muted); margin-bottom: 32px; }
        .breadcrumb a { color: var(--gold); }
        .product-detail { display: grid; grid-template-columns: 1fr 1fr; gap: 60px; margin-bottom: 60px; }
        .product-detail__gallery {}
        .product-detail__main-img {
          aspect-ratio: 1; border-radius: var(--radius-lg); overflow: hidden;
          background: var(--cream); margin-bottom: 12px;
        }
        .product-detail__main-img img { width: 100%; height: 100%; object-fit: cover; }
        .product-detail__main-img video { width: 100%; height: 100%; object-fit: cover; background: #000; }
        .product-detail__placeholder { width: 100%; height: 100%; display: flex; align-items: center; justify-content: center; font-size: 8rem; }
        .product-detail__thumbs { display: flex; gap: 10px; flex-wrap: wrap; }
        .product-detail__thumb {
          position: relative;
          width: 72px; height: 72px; border-radius: 10px; overflow: hidden;
          border: 2px solid transparent; background: var(--cream); transition: border-color 0.2s;
          cursor: pointer;
        }
        .product-detail__thumb.active { border-color: var(--gold); }
        .product-detail__thumb img,
        .product-detail__thumb video { width: 100%; height: 100%; object-fit: cover; display: block; }
        .product-detail__thumb-play {
          position: absolute; inset: 0; display: flex; align-items: center; justify-content: center;
          background: rgba(0,0,0,0.32); color: white; pointer-events: none;
        }
        .product-detail__category { font-size: 0.8rem; text-transform: uppercase; letter-spacing: 1px; color: var(--gold); font-weight: 600; margin-bottom: 8px; }
        .product-detail__name { font-family: var(--font-serif); font-size: 2rem; margin-bottom: 4px; }
        .product-detail__name-ar { font-size: 1.1rem; color: var(--text-muted); margin-bottom: 12px; direction: rtl; }
        .product-detail__rating { display: flex; align-items: center; gap: 8px; margin-bottom: 16px; }
        .product-detail__rating .stars { color: var(--gold); font-size: 1rem; }
        .product-detail__rating .count { font-size: 0.85rem; color: var(--text-muted); }
        .product-detail__price { display: flex; align-items: baseline; gap: 14px; margin-bottom: 16px; }
        .price-main { font-size: 1.8rem; font-weight: 700; color: var(--olive); }
        .price-compare { font-size: 1.1rem; color: var(--text-muted); text-decoration: line-through; }
        .product-detail__short-desc { color: var(--text-muted); line-height: 1.7; margin-bottom: 24px; }
        .product-detail__variants { margin-bottom: 24px; }
        .variant-label { font-weight: 600; margin-bottom: 10px; font-size: 0.9rem; }
        .variant-options { display: flex; gap: 10px; flex-wrap: wrap; }
        .variant-btn {
          padding: 8px 18px; border-radius: 50px; border: 2px solid var(--border);
          font-size: 0.9rem; font-weight: 500; transition: all 0.2s; font-family: var(--font-sans);
        }
        .variant-btn:hover { border-color: var(--gold); }
        .variant-btn.active { background: var(--gold); border-color: var(--gold); color: white; }
        .product-detail__actions { display: flex; gap: 14px; margin-bottom: 24px; }
        .qty-control {
          display: flex; align-items: center; gap: 12px; background: var(--cream-dark);
          border-radius: 50px; padding: 10px 16px;
        }
        .qty-control button { font-size: 1.2rem; font-weight: 700; color: var(--text); }
        .qty-control button:disabled { opacity: 0.4; cursor: not-allowed; }
        .qty-control span { font-weight: 700; min-width: 24px; text-align: center; }
        .product-detail__stock { font-size: 0.9rem; font-weight: 600; margin: -10px 0 24px; }
        .product-detail__stock.ok  { color: var(--olive); }
        .product-detail__stock.low { color: #d97706; }
        .product-detail__stock.out { color: #dc2626; }
        .product-detail__meta { display: flex; flex-direction: column; gap: 6px; margin-bottom: 20px; font-size: 0.85rem; color: var(--text-muted); }
        .product-detail__trust { display: flex; gap: 16px; flex-wrap: wrap; }
        .product-detail__trust span { font-size: 0.8rem; color: var(--olive); }
        .product-detail__description { background: var(--white); border-radius: var(--radius-lg); padding: 36px; margin-bottom: 40px; }
        .product-detail__description h3 { font-family: var(--font-serif); margin-bottom: 16px; }
        .product-detail__description p { line-height: 1.8; color: var(--text-muted); }
        .product-detail__reviews h3 { font-family: var(--font-serif); margin-bottom: 24px; }
        .reviews-list { display: flex; flex-direction: column; gap: 16px; }
        .review-card { background: var(--white); border-radius: var(--radius); padding: 20px; }
        .review-card__header { display: flex; align-items: center; gap: 12px; margin-bottom: 8px; flex-wrap: wrap; }
        .review-stars { color: var(--gold); }
        .review-title { font-weight: 600; margin-bottom: 6px; }
        .review-body { color: var(--text-muted); line-height: 1.6; font-size: 0.9rem; }
        @media (max-width: 768px) { .product-detail { grid-template-columns: 1fr; gap: 30px; } }
      `}</style>
    </div>
  );
};

export default ProductPage;
